import type { ToolDefinition, ToolType } from "../types";

const IMAGE_TO_HEIC_TOOL_TYPE = { label: "Apple Format Converter", icon: "HardDrive" } as const satisfies ToolType;

const imageToHeicTool: ToolDefinition = {
    slug: "image-to-heic",
    toolType: IMAGE_TO_HEIC_TOOL_TYPE,
    heroBadges: [
      "100% Free",
      "Adjustable Quality",
      "Smaller File Sizes",
      "No Watermark",
      "No Account Required",
      "Secure Processing"
    ],
    name: "Image to HEIC Converter Online | Convert JPG, PNG to HEIC — Adjustable Quality",
    description: "Convert JPG, PNG, or WebP images to HEIC format with an adjustable quality slider (10–100). Default quality 80 matches iPhone's native HEIC setting.",
    longDescription: "Convert images to HEIC format to reduce storage space while maintaining high visual quality. Quality slider ranges from 10 (maximum compression) to 100 (maximum fidelity) — default 80 matches the quality level Apple's own iPhone cameras use when shooting HEIC. HEIC produces files 40–50% smaller than JPEG at equivalent quality. Accepts JPG, PNG, WebP, BMP, GIF, and TIFF. Free, no watermark.",
    category: "Conversion Tools",
    categorySlug: "conversion-tools",
    href: "/tools/image-to-heic",
    api: "/image-to-heic",
    keywords: [
        "image to heic",
        "jpg to heic",
        "png to heic",
        "apple photo converter",
        "heic format generator",
        "save photo as heic",
        "snappy fix heic converter",
        "smaller photo format tool",
        "iphone compatible image converter",
        "webp to heic",
        "modern image format creator",
        "high efficiency photo tool"
      ],
    pageContent: {
      sections: {
        steps: {
          title: "How to Convert an Image to HEIC",
          description: "Four steps. Quality control. Apple-ready output."
        },
        benefits: {
          title: "Why Use the Snappy-Fix Image to HEIC Converter?",
          description: "Quality control, broad format support, and genuine file size reduction — not just a format rename."
        },
        faqs: {
          description: "Everything you need to know about converting images to HEIC format and choosing the right quality setting."
        }
      },
      intro: {
        title: "Why Convert Images to HEIC Format?",
        paragraphs: [
          "HEIC (High Efficiency Image Container) is the image format Apple introduced with iOS 11 — and for good reason. Using the HEVC compression algorithm, HEIC stores images at roughly half the file size of JPEG while maintaining equivalent or better visual quality. iPhones shoot in HEIC by default because it lets them store twice as many photos in the same storage space without any perceptible drop in image quality.",
          "If you have a library of JPG or PNG images you want to archive more efficiently, store on an iPhone, sync to iCloud, or simply reduce the storage footprint of, converting to HEIC is the most effective format choice. This tool adds a quality slider so you can precisely control the compression level: higher quality for archival use, lower quality for maximum space savings.",
        ],
      },
      callouts: [
        {
          title: "Who converts to HEIC",
          iconColor: "primary",
          items: [
            { label: "Apple users archiving existing JPG libraries to save storage", icon: "HardDrive" },
            { label: "Photographers moving images from Windows cameras to iPhone", icon: "Camera" },
            { label: "iCloud users reducing their storage footprint", icon: "FolderArchive" },
            { label: "Developers preparing images for iOS app assets", icon: "Code2" },
            { label: "Content creators building Apple-native image libraries", icon: "ImageIcon" },
            { label: "Families backing up photos to Apple-native formats", icon: "Smartphone" },
          ],
        },
        {
          title: "When HEIC is the right choice",
          iconColor: "accent",
          items: [
            { label: "Storing images on iPhone, iPad, or Mac natively", icon: "Smartphone", accent: "accent" },
            { label: "Uploading to iCloud where HEIC is preferred", icon: "HardDrive", accent: "accent" },
            { label: "Archiving large photo libraries with reduced storage cost", icon: "FolderArchive", accent: "accent" },
            { label: "Reducing the size of exports from non-Apple cameras", icon: "Camera", accent: "accent" },
            { label: "Building iOS apps where HEIC assets load efficiently", icon: "Code2", accent: "accent" },
            { label: "When you specifically need Apple-ecosystem compatibility", icon: "Globe", accent: "accent" },
          ],
        },
      ],
      referenceTables: [
        {
          title: "Quality setting guide",
          columns: [
            "Quality range",
            "Level",
            "Best for",
            "Approx. file size",
          ],
          emphasisColumns: ["Approx. file size"],
          monospaceColumns: ["Quality range"],
          rows: [
            {
              "Quality range": "90–100",
              Level: "Maximum quality",
              "Best for": "Archival, professional photography",
              "Approx. file size": "Largest",
            },
            {
              "Quality range": "80",
              Level: "iPhone default",
              "Best for": "Daily photography, general use",
              "Approx. file size": "50% smaller than JPEG",
            },
            {
              "Quality range": "60–70",
              Level: "High efficiency",
              "Best for": "Storage-conscious sharing",
              "Approx. file size": "60% smaller than JPEG",
            },
            {
              "Quality range": "40–60",
              Level: "Web / previews",
              "Best for": "Thumbnails, low-bandwidth use",
              "Approx. file size": "Smallest practical",
            },
            {
              "Quality range": "10–30",
              Level: "Maximum compression",
              "Best for": "Only where size is critical",
              "Approx. file size": "Very small, visible artefacts",
            },
          ],
        },
        {
          title: "File size comparison — 4MB JPEG source",
          columns: [
            "Format",
            "Approx. size",
            "Quality impact",
            "Platform support",
          ],
          emphasisColumns: ["Approx. size"],
          rows: [
            {
              Format: "JPG (source)",
              "Approx. size": "4.0 MB",
              "Quality impact": "Baseline",
              "Platform support": "Universal",
            },
            {
              Format: "HEIC quality 100",
              "Approx. size": "~2.8 MB",
              "Quality impact": "Lossless vs JPG",
              "Platform support": "Apple native",
            },
            {
              Format: "HEIC quality 80",
              "Approx. size": "~1.8 MB",
              "Quality impact": "Equivalent to JPG",
              "Platform support": "Apple native",
            },
            {
              Format: "HEIC quality 60",
              "Approx. size": "~1.2 MB",
              "Quality impact": "Minor reduction",
              "Platform support": "Apple native",
            },
            {
              Format: "PNG (lossless)",
              "Approx. size": "~9.0 MB",
              "Quality impact": "Lossless",
              "Platform support": "Universal",
            },
          ],
        },
      ],
      faqs: [
  {
    question: "What is HEIC and why would I want to convert to it?",
    answer:
      "HEIC (High Efficiency Image Container) is Apple's modern image format that uses the HEVC compression algorithm to store images at roughly half the file size of JPEG while maintaining equivalent or better visual quality. If you have images stored as JPG or PNG that you want to save to an iPhone or iPad, archive on Apple hardware, or simply reduce in storage size without noticeable quality loss, converting to HEIC achieves all of these goals — producing significantly smaller files that integrate natively with Apple's ecosystem.",
  },
  {
    question: "What does the quality slider control?",
    answer:
      "The quality slider sets the HEIC compression level on a scale of 10 to 100. A value of 100 applies minimal compression — the output file is larger but preserves maximum detail. A value of 10 applies maximum compression — the file is smallest but some visual detail is lost. The default is 80, which is the standard used by iPhones when shooting in HEIC mode — it strikes the optimal balance between file size reduction and image quality that Apple's own engineers chose for daily photography.",
  },
  {
    question: "How much smaller will my file be after converting to HEIC?",
    answer:
      "At the default quality of 80, HEIC typically produces files that are 40–50% smaller than an equivalent JPEG at the same visual quality. A 4MB JPEG photograph commonly becomes a 1.5–2MB HEIC file. At quality 60, reductions of 60–70% are common. The exact result depends on the content of the image — photographs with complex colour gradients benefit more from HEIC compression than simple flat-colour graphics.",
  },
  {
    question: "Can I open HEIC files on Windows or Android after converting?",
    answer:
      "HEIC has limited support on non-Apple platforms. Windows 10 and 11 require a paid codec extension from the Microsoft Store to open HEIC files natively. Android does not support HEIC natively. Most Windows apps (Photoshop, Lightroom) support HEIC. If you need the converted file to work on Windows or Android without additional software, convert to JPG or PNG instead. If you are archiving for Apple devices or iCloud, HEIC is the right choice.",
  },
  {
    question: "What image formats can I convert to HEIC?",
    answer:
      "The tool accepts all standard web image formats: JPG, JPEG, PNG, WebP, BMP, GIF, and TIFF. Any image you upload is converted to HEIC at your chosen quality level regardless of the source format.",
  },
  {
    question: "Will converting a JPEG to HEIC improve quality?",
    answer:
      "No. Converting from JPEG to HEIC does not recover quality that JPEG compression already discarded. The HEIC output will be lossless relative to the JPEG source — but the quality ceiling is the JPEG you start with, not the original uncompressed image. The benefit of converting JPEG to HEIC is purely file size: you get a smaller file at equivalent visual quality to the JPEG. If you want to improve quality, you need to start from a higher-resolution or less-compressed source.",
  },
  {
    question: "What quality setting should I use?",
    answer:
      "For general photography storage and sharing: use 80 (the default — matches iPhone's own HEIC quality). For archival where quality is paramount: use 90–100. For maximum storage savings where minor quality reduction is acceptable: use 60–70. For web thumbnails or previews where file size matters most: use 40–60. Avoid values below 30 unless you specifically need the smallest possible file — compression artefacts become noticeable below this threshold for most photographs.",
  },
  {
    question:
      "Is the Image to HEIC converter free and does it add a watermark?",
    answer:
      "Yes — completely free with no usage limits and no watermark on converted images. Convert as many images as you need at no cost with no account required.",
  },
  {
    question: "Are my uploaded images stored on your servers?",
    answer:
      "No. Your image is uploaded for conversion and the HEIC result is returned to your browser. No copy of your original or converted image is retained on our servers after the conversion is complete.",
  },
],
      benefits: [
  {
    icon: "HardDrive",
    title: "40–50% smaller files",
    description:
      "HEIC typically produces files 40–50% smaller than JPEG at equivalent quality — freeing storage on iCloud, devices, and backup drives.",
  },
  {
    icon: "Sliders",
    title: "Adjustable quality slider",
    description:
      "Set compression from 10 to 100. The default of 80 matches iPhone's native HEIC quality — or go higher for archival, lower for maximum savings.",
  },
  {
    icon: "FileImage",
    title: "Any format to HEIC",
    description:
      "Converts JPG, PNG, WebP, BMP, TIFF, and GIF to HEIC — not just JPEG. Bring any image into Apple's efficient storage format.",
  },
  {
    icon: "Zap",
    title: "Instant conversion",
    description:
      "Upload and convert in seconds. No processing queue, no waiting — your HEIC file is ready to download immediately.",
  },
  {
    icon: "ShieldCheck",
    title: "Private and secure",
    description:
      "Your images are processed securely and never permanently stored. Original and converted files are discarded after the session ends.",
  },
  {
    icon: "Globe",
    title: "Works on any device",
    description:
      "Fully responsive — convert images to HEIC on desktop, laptop, tablet, or smartphone from any modern browser.",
  },
],
      steps: [
  {
    number: "01",
    title: "Set your quality level",
    description:
      "Before uploading, adjust the HEIC Quality slider at the top of the tool. The default of 80 matches iPhone's own HEIC compression — the sweet spot between file size and visual quality used by Apple's engineers.",
  },
  {
    number: "02",
    title: "Upload your image",
    description:
      "Drag and drop your image onto the upload zone or click to browse. A live preview loads immediately. Supported formats: JPG, PNG, WebP, BMP, TIFF, and GIF.",
  },
  {
    number: "03",
    title: "Click Convert to HEIC",
    description:
      "Hit the button and watch the progress bar as your image is uploaded and converted. The conversion processes server-side and completes in seconds.",
  },
  {
    number: "04",
    title: "Download your HEIC file",
    description:
      'Click "Download HEIC Image" to save the converted file to your device. The HEIC file is ready to use on iPhone, iPad, Mac, or iCloud immediately.',
  },
],
    },
  };

export default imageToHeicTool;
export { imageToHeicTool };
